// This is the card that will display the jobsite information.
//  It will display the jobsite name, address, notes, and a few icons that will allow the user to interact with the jobsite.
import { useState } from "react";
import { LuEllipsis, LuUsers } from "react-icons/lu";
import { RiStickyNoteAddLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import NotesInCards from "./NotesInCards";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteJobsite, toggleJobsiteStatus  } from "../features/jobsites/jobsitesSlice";
import SelectForemanModal from "./SelectForemanModal";
import KioskAccessModal from "./KioskAccessModal";
import { setJobsiteId } from "../features/kioskManagement/kioskManagementSlice";

const JobSiteCard = ({ jobSite, tradeData }) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDropdown, setShowDropdown] = useState(false);
    const topTrades = tradeData?.topTrades ?? [];
    const totalOnSite = tradeData?.total ?? 0;
    const [editNotesClicked, setEditNotesClicked]= useState(false);

    // Modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
    const [showForemanModal, setShowForemanModal] = useState(false);
    const [showKioskModal, setShowKioskModal] = useState(false);

    //toggle the edit notes button
    const handleEditNotes = () => { 
        setEditNotesClicked(!editNotesClicked);
       
    }

    const handleCogClick = () => {
     
      setShowDropdown(!showDropdown); 

    }

  
    //Navigate to he jobsite/attendance page
    const handleViewAttendance = () => {
     
      navigate('/jobsite/attendance/', {
          state: { jobsiteID: jobSite.id, jobsiteName: jobSite.jobsiteName }
      });
    } ;

    //deletes the jobsite from the database, ask the user if they are sure they want to delete the jobsite.
    const handleDeleteJobsite =  () => { 
      setShowDeleteModal(true);
    }

    // Confirm delete action
    const handleConfirmDelete = () => {
      if (deleteConfirmInput === jobSite.jobsiteName) {
        dispatch(deleteJobsite(jobSite.id)); // Ensure jobSite.id is passed
        setShowDeleteModal(false);
        setDeleteConfirmInput("");
      }
    }

    const handleSelectForeman = () => {
      setShowForemanModal(true);
    }

    const handleSelectKioskAccess = () => {
      dispatch(setJobsiteId(jobSite.id));
      setShowKioskModal(true);
    }

    // Handler for Pause button
    const handleToggleStatus = () => {
      dispatch(toggleJobsiteStatus(jobSite.id));
      setShowDropdown(false); // Close the dropdown after toggling status
    }

    return (
  <>
    <div className="rounded-xl border border-border bg-surface shadow-sm overflow-hidden transition-shadow hover:shadow-md w-full">

      {/* Header band — 96px, shows site photo as background if available */}
      <div
        className="relative h-24 bg-ink flex flex-col justify-between px-4 py-3"
        style={jobSite.s3URL ? { backgroundImage: `url(${jobSite.s3URL})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        {jobSite.s3URL && <div className="absolute inset-0 bg-ink/75" />}

        {/* Status pill + kebab */}
        <div className="relative z-10 flex items-center justify-between">
          {jobSite.isActive ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-900/50 text-green-300 text-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-eyebrow">
              <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
              Paused
            </span>
          )}

          <div className="relative">
            <button
              className="p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              onClick={handleCogClick}
            >
              <LuEllipsis size={16} />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-1 z-20 w-48 rounded-lg border border-border bg-surface shadow-md overflow-hidden">
                <div className="py-1" role="menu">
                  <button
                    type="button"
                    disabled={!jobSite.isActive}
                    className={`w-full text-left px-4 py-2 text-body transition-colors ${jobSite.isActive ? 'text-ink-2 hover:bg-surface-2' : 'text-ink-4 cursor-not-allowed'}`}
                    onClick={handleSelectForeman}
                  >
                    View Foremen
                  </button>
                  <button
                    type="button"
                    disabled={!jobSite.isActive}
                    className={`w-full text-left px-4 py-2 text-body transition-colors ${jobSite.isActive ? 'text-ink-2 hover:bg-surface-2' : 'text-ink-4 cursor-not-allowed'}`}
                    onClick={handleSelectKioskAccess}
                  >
                    Kiosk Access
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-body text-ink-2 hover:bg-surface-2 transition-colors"
                    onClick={handleToggleStatus}
                  >
                    {jobSite.isActive ? 'Pause Jobsite' : 'Activate Jobsite'}
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-body text-critical hover:bg-critical/5 border-t border-border transition-colors"
                    onClick={handleDeleteJobsite}
                  >
                    Delete Jobsite
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trade breakdown — bottom of header band */}
        <div className="relative z-10 flex items-end justify-between">
          <div className="flex flex-wrap gap-1.5">
            {topTrades.length > 0 ? (
              topTrades.map(({ role, count }) => (
                <span
                  key={role}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white text-eyebrow"
                >
                  <span className="truncate max-w-[6rem]">{role}</span>
                  <span className="font-mono text-white/70">· {count}</span>
                </span>
              ))
            ) : (
              <span className="text-eyebrow text-white/30">No activity yet</span>
            )}
          </div>
          {totalOnSite > 0 && (
            <span className="text-eyebrow font-mono text-white/50 shrink-0 ml-2">
              {totalOnSite} total
            </span>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className={`p-4 ${!jobSite.isActive ? 'opacity-50' : ''}`}>

        {/* Jobsite name + address */}
        <h2 className="text-card-title text-ink truncate">{jobSite.jobsiteName}</h2>
        <p
          className="text-secondary text-ink-3 mt-0.5 truncate cursor-pointer hover:underline"
          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(jobSite.address.split(', USA')[0])}`, '_blank')}
        >
          {jobSite.address.split(', USA')[0]}
        </p>

        {/* Notes */}
        <div className="mt-3">
          {editNotesClicked ? (
            <NotesInCards jobSite={jobSite} setEditNotesClicked={setEditNotesClicked} />
          ) : (
            <div className="rounded-lg bg-surface-2 p-3 overflow-y-auto max-h-28 min-h-[4rem]">
              {jobSite.siteNotes && Object.keys(jobSite.siteNotes).length > 0 ? (
                Object.entries(jobSite.siteNotes).map(([key, value]) => (
                  <p key={key} className="text-body text-ink-2">
                    <span className="font-medium text-ink">{key}:</span> {value}
                  </p>
                ))
              ) : (
                <p className="text-secondary text-ink-4 text-center pt-2">Add a note for the team.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer: attendance link + notes button */}
        {!editNotesClicked && (
          <div className="mt-3 flex items-center justify-between">
            <button className="relative group flex items-center gap-1.5 text-ink-3 hover:text-ink transition-colors" onClick={handleViewAttendance}>
              <LuUsers size={14} />
              <span className="text-secondary">Attendance</span>
            </button>

            <button
              className="relative group p-1.5 rounded-md text-ink-3 hover:bg-surface-2 transition-colors"
              onClick={handleEditNotes}
            >
              <RiStickyNoteAddLine size={16} />
              <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md bg-ink text-white text-eyebrow opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-sm">
                Edit Notes
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40">
          <div className="bg-surface rounded-xl border border-border shadow-md p-6 max-w-sm w-full">
            <h2 className="text-section text-critical mb-2">Delete Jobsite</h2>
            <p className="text-body text-ink-2 mb-4">
              Deleting this jobsite is <span className="font-medium text-ink">permanent</span> and should only be done if your project is complete. To confirm, type{' '}
              <span className="font-mono bg-surface-2 px-1 rounded text-secondary text-ink">{jobSite.jobsiteName}</span> below:
            </p>
            <input
              className="border border-border rounded-lg px-3 py-2 w-full mb-4 text-body text-ink bg-surface focus:outline-none focus:ring-2 focus:ring-brand/40"
              value={deleteConfirmInput}
              onChange={e => setDeleteConfirmInput(e.target.value)}
              placeholder="Type jobsite name to confirm"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 rounded-lg text-label text-ink-2 border border-border hover:bg-surface-2 transition-colors"
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmInput(''); }}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-1.5 rounded-lg text-label text-white transition-colors ${deleteConfirmInput === jobSite.jobsiteName ? 'bg-critical hover:bg-critical/90' : 'bg-critical/30 cursor-not-allowed'}`}
                disabled={deleteConfirmInput !== jobSite.jobsiteName}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <SelectForemanModal isOpen={showForemanModal} onClose={() => setShowForemanModal(false)} jobsiteId={jobSite.id} />
      <KioskAccessModal isOpen={showKioskModal} onClose={() => setShowKioskModal(false)} jobsiteId={jobSite.id} />
    </div>
  </>
    );
  }
  
  export default JobSiteCard;
